import axios from 'axios';
import store from '@/store';
import {
  PIN_SESSION_HEADER,
  PIN_REQUIRED_CODE,
  isPinGatedPath,
  pinPromptLocation,
} from '@/utils/giving-pin-session';

/**
 * Attaches the MyGiving PIN session to giving-api requests, and sends the member
 * back to the PIN screen when giving-api says the session is missing or expired.
 *
 * Only giving-api hosts (VUE_APP_GIVING_API_URL / _V2_URL) get the header: it
 * means nothing to any other host and is never sent to third parties. hogc-one-api
 * calls that need it (/giving/pin/state, /giving/pin/session/end) set it themselves
 * in the myGiving store.
 */

const givingApiOrigins = () => {
  const origins = new Set();
  for (const url of [process.env.VUE_APP_GIVING_API_URL, process.env.VUE_APP_GIVING_API_V2_URL]) {
    if (!url) continue;
    try {
      origins.add(new URL(url).origin);
    } catch (_) {
      // ignore malformed env URL
    }
  }
  return origins;
};

/** @returns {boolean} true when the request is bound for giving-api */
export const isGivingApiUrl = (requestUrl, baseURL) => {
  if (!requestUrl && !baseURL) return false;
  try {
    return givingApiOrigins().has(new URL(requestUrl || '', baseURL || undefined).origin);
  } catch (_) {
    return false;
  }
};

let requestInterceptorId = null;
let responseInterceptorId = null;

/**
 * A second PIN_REQUIRED for the same page this soon after sending the member to
 * the PIN screen means the PIN did not produce a usable session (e.g. an
 * hogc-one-api that predates sessions): stop redirecting instead of looping.
 */
const REDIRECT_LOOP_WINDOW_MS = 10_000;
const PIN_AGAIN_MESSAGE = 'Please enter your MyGiving PIN again.';
let lastRedirect = { fullPath: null, at: 0 };

const isRepeatRedirect = (fullPath, now) =>
  lastRedirect.fullPath === fullPath && now - lastRedirect.at < REDIRECT_LOOP_WINDOW_MS;

export function removeGivingPinSessionInterceptor() {
  if (requestInterceptorId !== null) axios.interceptors.request.eject(requestInterceptorId);
  if (responseInterceptorId !== null) axios.interceptors.response.eject(responseInterceptorId);
  requestInterceptorId = null;
  responseInterceptorId = null;
  lastRedirect = { fullPath: null, at: 0 };
}

/**
 * @param {{ router?: { currentRoute: { value: { fullPath: string, path: string } }, replace: Function } }} options
 */
export function initGivingPinSessionInterceptor({ router } = {}) {
  removeGivingPinSessionInterceptor();

  requestInterceptorId = axios.interceptors.request.use(config => {
    try {
      const sessionId = store?.state?.myGiving?.pinSessionId;
      if (sessionId && isGivingApiUrl(config.url, config.baseURL)) {
        config.headers = { ...(config.headers || {}), [PIN_SESSION_HEADER]: sessionId };
      }
    } catch (err) {
      console.error('[GivingPinSession] request interceptor error:', err);
    }
    return config;
  });

  responseInterceptorId = axios.interceptors.response.use(
    response => response,
    error => {
      try {
        const isPinRequired =
          error?.response?.status === 403 &&
          error.response.data?.code === PIN_REQUIRED_CODE &&
          isGivingApiUrl(error.config?.url, error.config?.baseURL);
        if (isPinRequired) {
          store.commit('myGiving/clearPinSession');
          const current = router?.currentRoute?.value;
          const now = Date.now();
          // Only from a PIN-gated page: the landing page and the PIN screens
          // handle their own state, and redirecting from them would loop.
          if (current && isPinGatedPath(current.path) && !isRepeatRedirect(current.fullPath, now)) {
            lastRedirect = { fullPath: current.fullPath, at: now };
            router.replace(pinPromptLocation(current.fullPath)).catch(() => {});
          } else {
            // Not a MyGiving page (e.g. the course/product payment sheet), or a
            // repeat: say why the action failed; the sheet re-asks for the PIN.
            store.dispatch('toast/error', PIN_AGAIN_MESSAGE, { root: true });
          }
        }
      } catch (err) {
        console.error('[GivingPinSession] response interceptor error:', err);
      }
      return Promise.reject(error);
    },
  );
}
